import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import PaymentSummary from "./PaymentSummary";
import PaymentSummaryPlain from "./PaymentSummary/PaymentSummaryPlain.jsx";
import { useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { createPaymentIntentAPI } from "../services/checkout";
import { selectUser } from "../store/slice/user";

const stripePromise = loadStripe("pk_live_51S5vZsJ0qH1UUVsoU8Ek8UT8wI0jSNGJHKTWGb3UQDHQmJBcIEm2QHkwg8mD79HV71LdaYpSJXZVINK5Ebosqnpw00Ej9CaS2L");

const PaymentSummaryWrapper = () => {
  const location = useLocation();
  const { token } = useSelector(selectUser);

  const isSeating = location.state?.isSeating === true;
  // const eventSlug = location.state?.eventSlug;
  const eventSlug = location.state?.eventSlug ?? { data: location.state?.eventData, ticketsData: null };
console.log("the state of event slug is ", eventSlug)
  const initialCoupon = location.state?.couponValue ?? "";

  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [summary, setSummary] = useState(null); // ✅ store summary
  const [activeCoupon, setActiveCoupon] = useState(initialCoupon);
  const [isFree, setIsFree] = useState(false); // coupon covers the full amount, nothing to charge
  const [loadError, setLoadError] = useState(false);

  const isStripeCurrency = !isSeating &&
    eventSlug?.data?.tickets?.[0]?.currency?.toUpperCase() === "GBP";

  // ✅ Function to create a new PaymentIntent
  const createPaymentIntent = useCallback(
    async (coupon = "") => {
      const response = await createPaymentIntentAPI(
        {
          event: eventSlug.data.id,
          tickets: eventSlug.ticketsData,
          coupon, // include latest coupon
        },
        token
      );

      const data = response.data;
      setClientSecret(data.client_secret);
      setPaymentIntentId(data.payment_intent_id);
      setSummary(data.summary); // ✅ keep summary available
    },
    [eventSlug, token]
  );

  // ✅ Create initial PaymentIntent
  useEffect(() => {
    if (!eventSlug || !isStripeCurrency) return;
    createPaymentIntent(initialCoupon).catch((err) => {
      console.error("Error creating PaymentIntent:", err);
      setLoadError(true);
    });
  }, [createPaymentIntent, initialCoupon, isStripeCurrency]);

  // ✅ Coupon was already validated by PaymentSummary. The current checkout stays
  // on screen until the discounted PaymentIntent is ready; if that fails the error
  // is thrown back to PaymentSummary and the existing intent stays usable.
  const handleCouponApplied = async (coupon, couponSummary) => {
    if (couponSummary && Number(couponSummary.total) <= 0) {
      // Stripe can't create a PaymentIntent for 0 — order is completed as free
      setSummary(couponSummary);
      setActiveCoupon(coupon);
      setIsFree(true);
      return;
    }
    await createPaymentIntent(coupon);
    setActiveCoupon(coupon);
  };

  // ✅ Render logic
  if (isStripeCurrency) {
    if (!clientSecret) {
      return (
        <div>
          {loadError
            ? "Unable to load payment checkout. Please go back and try again."
            : "Loading payment checkout..."}
        </div>
      );
    }

    return (
      <Elements 
        key={clientSecret} // new intent => fresh Elements instance
        stripe={stripePromise}
        options={{ 
          clientSecret,
          appearance: {
            theme: 'stripe',
            layout: { 
              maxHeight: "700px",
              mobileLayout: "vertical"
            },
            rules: {
              '.PaymentMethod-TabList': { display: 'flex' },     // ✅ Fixed
              '.PaymentMethod-klarna': { display: 'flex' }       // ✅ Fixed
            }
          }
        }}
      >

        <PaymentSummary
          clientSecret={clientSecret}
          PID={paymentIntentId}
          summary={summary} // ✅ now correctly passed
          isStripeCurrency={true}
          appliedCoupon={activeCoupon}
          isFree={isFree}
          onCouponApplied={handleCouponApplied}
        />
      </Elements>
    );
  }

  if (isSeating) {
    return (
      <PaymentSummaryPlain
        eventSlug={eventSlug}
        couponValue={activeCoupon}
        isSeating={true}
        seatingState={location.state}
      />
    );
  }

  return (
    <PaymentSummaryPlain eventSlug={eventSlug} couponValue={activeCoupon} />
  );
};

export default PaymentSummaryWrapper;
